(() => {
  const selectedVariant = (root) => {
    const input = root?.querySelector('product-form input[name="id"]');
    if (!input?.value) return null;
    try {
      const variant = JSON.parse(root.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null');
      return String(variant?.id) === input.value ? variant : null;
    } catch { return null; }
  };
  class VariantComponent extends HTMLElement {
    connectedCallback() {
      cancelAnimationFrame(this.initialization);
      this.initialization = requestAnimationFrame(() => {
        if (this.isConnected) this.initialize();
      });
    }
    initialize() {
      this.events?.abort();
      this.events = new AbortController();
      this.productInfo = this.closest('product-info');
      this.productInfo?.addEventListener('change', (event) => {
        if (event.target.matches('product-form input[name="id"]')) this.variantChanged(selectedVariant(this.productInfo));
      }, { signal: this.events.signal });
      this.setup?.();
    }
    disconnectedCallback() { cancelAnimationFrame(this.initialization); this.events?.abort(); }
  }
  if (!customElements.get('gw-vault-pipeline')) {
    customElements.define('gw-vault-pipeline', class extends VariantComponent {
      setup() {
        this.selection = this.querySelector('[data-vault-selection]');
        this.baseSelection = this.dataset.selection || this.selection?.textContent.trim();
        this.dataset.selection = this.baseSelection;
        this.productInfo?.addEventListener('input', (event) => {
          if (event.target.matches('gw-ring-size input')) this.renderSelection();
        }, { signal: this.events.signal });
        this.renderSelection();
      }
      variantChanged(variant) {
        this.baseSelection = variant
          ? (this.dataset.defaultVariant === 'true' ? this.dataset.productTitle : variant.title)
          : this.dataset.unavailableLabel;
        this.dataset.selection = this.baseSelection;
        this.renderSelection();
      }
      renderSelection() {
        if (!this.selection) return;
        const size = this.productInfo?.querySelector('gw-ring-size input');
        const requestedSize = size?.value && size.validity.valid ? ' · ' + size.labels[0].textContent.trim() + ': ' + size.value : '';
        this.selection.textContent = this.baseSelection + (this.baseSelection !== this.dataset.unavailableLabel ? requestedSize : '');
      }
    });
  }
  if (!customElements.get('gw-ready-made-request')) {
      customElements.define('gw-ready-made-request', class extends VariantComponent {
        setup() {
          this.variant = selectedVariant(this.productInfo);
          this.button = this.querySelector('button[type="submit"]');
          this.productInfo?.addEventListener('input', event => {
            if (event.target.name === 'quantity') this.renderRequest();
          }, {signal: this.events.signal});
          this.querySelector('form')?.addEventListener('submit', event => {
            this.variant = selectedVariant(this.productInfo);
            if (!this.renderRequest()) event.preventDefault();
          }, {signal: this.events.signal});
          this.renderRequest();
        }
        variantChanged(variant) { this.variant = variant; this.renderRequest(); }
        renderRequest() {
          if (!this.button) return false;
          const quantityInput = this.productInfo?.querySelector('input[name="quantity"]');
          const quantity = Number(quantityInput?.value || 1);
          const valid = this.variant?.available && Number.isInteger(quantity) && quantity > 0 && (!quantityInput || quantityInput.validity.valid);
          this.button.disabled = !valid;
          const summary = this.querySelector('[data-ready-summary]');
          if (!valid) { if(summary) summary.textContent = this.dataset.unavailable; return false; }
          const id = String(this.variant.id);
          const text = `${this.dataset.title} · ${this.variant.title} · quantity ${quantity}`;
          summary.textContent = text;
          this.querySelector('[data-ready-id]').value = id;
          this.querySelector('[data-ready-sku]').value = this.variant.sku || '';
          this.querySelector('[data-ready-body]').value = `${text}\nSKU: ${this.variant.sku || ''}\n${this.dataset.url}?variant=${encodeURIComponent(id)}`;
          return true;
        }
      });
  }
  if (!customElements.get('gw-ring-size')) {
    customElements.define('gw-ring-size', class extends HTMLElement {
      connectedCallback() {
        cancelAnimationFrame(this.initialization);
        this.initialization = requestAnimationFrame(() => {
          if (this.isConnected) this.initialize();
        });
      }
      initialize() {
        this.events?.abort();
        this.events = new AbortController();
        this.input = this.querySelector('input');
        this.input.disabled = false;
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-ring-size]');
          if (!button) return;
          this.input.value = button.dataset.ringSize;
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
        }, { signal: this.events.signal });
        this.addEventListener('input', () => this.render(), { signal: this.events.signal });
        this.render();
      }
      render() {
        this.querySelectorAll('[data-ring-size]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.ringSize === this.input.value)));
      }
      disconnectedCallback() { cancelAnimationFrame(this.initialization); this.events?.abort(); }
    });
  }
  if (!customElements.get('gw-margin-calculator')) {
    customElements.define('gw-margin-calculator', class extends VariantComponent {
      setup() {
        this.currency = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
        this.cost = this.dataset.price === '' ? null : Number(this.dataset.price) / 100;
        this.extra = this.querySelector('[data-extra-cost]');
        this.retail = this.querySelector('[data-retail-price]');
        this.target = this.querySelector('[data-target-margin]');
        this.addEventListener('input', () => this.calculate(), { signal: this.events.signal });
        this.querySelector('[data-use-target]').addEventListener('click', () => {
          if (!Number.isFinite(this.targetPrice)) return;
          this.retail.value = this.targetPrice.toFixed(2);
          this.calculate();
        }, { signal: this.events.signal });
        this.querySelector('[data-quote-print]')?.addEventListener('click', () => this.printQuote(), { signal: this.events.signal });
        this.productInfo?.addEventListener('input', (event) => {if(event.target.matches('gw-ring-size input')) this.calculate();}, {signal:this.events.signal});
        this.calculate();
      }
      variantChanged(variant) {
        this.cost = variant && Number.isFinite(variant.price) ? variant.price / 100 : null;
        this.dataset.price = this.cost === null ? '' : String(variant.price);
        this.querySelector('fieldset').disabled = this.cost === null;
        this.querySelector('[data-mounting-cost]').textContent = this.cost === null ? this.dataset.unavailable : this.currency.format(this.cost);
        this.calculate();
      }
      printQuote() {
        const status = this.querySelector('[data-quote-status]');
        if (this.cost === null || !this.retail.validity.valid || !(Number(this.retail.value) > 0)) {
          status.textContent = this.dataset.quoteError;this.retail.focus();return;
        }
        status.textContent = '';
        const root = this.closest('product-info');
        const escape = (value) => { const span = document.createElement('span');span.textContent = value || '';return span.innerHTML; };
        const specs = [...(root?.querySelectorAll('gw-selected-specs [data-spec]:not([hidden])') || [])].map((row) => '<tr><th>'+escape(row.querySelector('dt').textContent)+'</th><td>'+escape(row.querySelector('dd').textContent)+'</td></tr>').join('');
        const title = root?.querySelector('h1,h2')?.textContent.trim() || '';
        const business = this.querySelector('[data-quote-business]').value.trim();
        const customer = this.querySelector('[data-quote-customer]').value.trim();
        const frame = document.createElement('iframe');frame.setAttribute('title', this.dataset.quoteTitle);frame.style.cssText='position:fixed;width:0;height:0;border:0';document.body.append(frame);
        const doc = frame.contentDocument;
        doc.open();doc.write('<!doctype html><html><head><meta charset="utf-8"><title>'+escape(this.dataset.quoteTitle)+'</title><style>body{font:15px Arial;color:#17140d;margin:48px}h1{font-size:25px}table{border-collapse:collapse;width:100%;margin:24px 0}th,td{text-align:left;padding:10px;border-bottom:1px solid #ddd}th{width:40%}p{line-height:1.6}.price{font-size:24px;font-weight:bold;margin:30px 0}</style></head><body><h1>'+escape(business || this.dataset.quoteTitle)+'</h1><p>'+escape(customer)+'</p><h2>'+escape(title)+'</h2><table>'+specs+'</table><p class="price">'+escape(this.dataset.quotePrice)+': '+escape(this.currency.format(Number(this.retail.value)))+'</p><p>'+escape(this.dataset.quoteScope)+'</p></body></html>');doc.close();
        frame.contentWindow.addEventListener('afterprint', () => frame.remove(), {once:true});
        requestAnimationFrame(() => { frame.contentWindow.focus();frame.contentWindow.print(); });
        setTimeout(() => frame.remove(), 60000);
      }
      calculate() {
        const extra = this.extra.value === '' ? 0 : Number(this.extra.value);
        const valid = this.cost !== null && Number.isFinite(extra) && extra >= 0 && this.extra.validity.valid;
        const requestedSize = this.productInfo?.querySelector('gw-ring-size input')?.value;
        const sizingCost = requestedSize ? 25 : 0;
        const total = valid ? this.cost + extra + sizingCost : null;
        const retail = Number(this.retail.value);
        const target = Number(this.target.value);
        this.querySelector('[data-target-label]').textContent = target + '%';
        this.targetPrice = valid ? Math.ceil((total / (1 - target / 100)) * 100 - 1e-8) / 100 : null;
        this.querySelector('[data-target-price]').textContent = valid ? this.currency.format(this.targetPrice) : this.dataset.empty;
        this.querySelector('[data-use-target]').disabled = !valid;
        const hasRetail = valid && retail > 0 && this.retail.validity.valid;
        const profit = hasRetail ? retail - total : null;
        this.querySelector('[data-profit]').textContent = hasRetail ? this.currency.format(profit) : this.dataset.empty;
        this.querySelector('[data-margin]').textContent = hasRetail ? (profit / retail * 100).toFixed(1) + '%' : this.dataset.empty;
        this.querySelector('.gw-margin__results').classList.toggle('gw-margin__results--loss', hasRetail && profit < 0);
      }
    });
  }
  if (!customElements.get('gw-selected-specs')) {
    customElements.define('gw-selected-specs', class extends VariantComponent {
      variantChanged(variant) {
        const out = { color: '', length: '', mm: '', stones: '', carats: '', grams: '', band: '' };
        const parts = String(variant?.sku || '').toUpperCase().replace(/CTS/g, 'CT').split('-').map((part) => part.trim().replace(/\s|["“”]/g, ''));
        const colors = JSON.parse(this.dataset.colors);
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          if (colors[part]) out.color = colors[part];
          if (/^\d+(\.\d+)?MM$/.test(part)) out.mm = part.replace('MM', '') + ' mm';
          if (/^\d+(\.\d+)?G$/.test(part)) out.grams = '≈ ' + part.replace('G', '') + ' g';
          if (/^(?:\d+(?:\.\d+)?|\.\d+)CT$/.test(part)) out.carats = part.replace('CT', '') + ' ct';
          if (/^\d+ST$/.test(part)) out.stones = part.replace('ST', '');
          if (/^\d+$/.test(part) && /^(?:\d+(?:\.\d+)?|\.\d+)CT$/.test(parts[i + 1] || '')) out.stones = part;
        }
        if (Number(variant?.weight_grams) > 0) out.grams = '≈ ' + Number(Number(variant.weight_grams).toFixed(3)) + ' g';
        if (parts[0] === 'TN' && /^\d+(\.\d+)?$/.test(parts[1] || '')) out.length = parts[1] + ' in';
        JSON.parse(this.dataset.optionNames).forEach((name, index) => {
          const value = variant?.options?.[index];
          if (!value) return;
          name = name.toLowerCase();
          if (name.includes('metal') || name === 'color' || name === 'colour') out.color = value;
          if (name.includes('stone') && name.includes('size')) out.mm = /mm/i.test(value) ? value : value + ' mm';
          if (name.includes('band') && name.includes('width')) out.band = /mm/i.test(value) ? value : value + ' mm';
          if (name.includes('length')) out.length = /(in|inch|")/i.test(value) ? value : value + ' in';
        });
        const stoneProfile = this.dataset.ringStoneProfile;
        const centerCarats = Number(out.carats.replace(' ct', ''));
        const hasPave = stoneProfile === 'halo-pave' || stoneProfile === 'pave-only';
        const hasHalo = stoneProfile === 'halo-pave' || stoneProfile === 'halo-only';
        const centerAndBand = centerCarats + (hasPave ? 0.24 : 0);
        out['center-and-band'] = hasPave && centerCarats > 0 ? '≈ ' + Number(centerAndBand.toFixed(3)) + ' ct' : '';
        out['total-with-halo'] = hasHalo && centerCarats > 0 ? '≈ ' + Number((centerAndBand + 0.125).toFixed(3)) + ' ct' : '';
        Object.entries(out).forEach(([key, value]) => {
          const row = this.querySelector('[data-spec="' + key + '"]');
          if (!row) return;
          row.hidden = !value;
          row.querySelector('dd').textContent = value;
        });
      }
    });
  }
})();
